
"use client"

import React, { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface NavItem {
  name: string
  id: string
  icon: LucideIcon
  description?: string
  isLink?: boolean
  href?: string
}

interface NavBarProps {
  items: NavItem[]
  className?: string
  activeTab: string
  onTabChange: (tabId: string) => void
  onLinkClick?: (href: string) => void
}

export function NavBar({ items, className, activeTab, onTabChange, onLinkClick }: NavBarProps) {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <div className={cn("relative flex justify-center", className)}>
      <div className="flex items-center justify-center gap-1 bg-black/20 backdrop-blur-xl border border-white/20 py-2 px-2 rounded-full shadow-2xl w-fit">
        {items.map((item) => {
          const Icon = item.icon
          const isActive = activeTab === item.id

          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.isLink && item.href && onLinkClick) {
                  onLinkClick(item.href)
                } else {
                  onTabChange(item.id)
                }
              }}
              className={cn(
                "relative cursor-pointer text-sm font-semibold px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 flex-shrink-0",
                "text-gray-300 hover:text-white hover:scale-105",
                isActive && "text-white scale-105",
              )}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className={cn(
                  "w-6 h-6 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center transition-all duration-200 border flex-shrink-0",
                  isActive 
                    ? "bg-white/20 border-white/30" 
                    : "bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-white/20"
                )}>
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="hidden sm:inline font-semibold text-xs sm:text-sm whitespace-nowrap">
                    {item.name}
                  </span>
                  {item.description && (
                    <span className="text-xs text-gray-400 hidden lg:block whitespace-nowrap">
                      {item.description}
                    </span>
                  )}
                </div>
              </div>
              
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-gradient-to-r from-blue-600/80 via-purple-600/80 to-cyan-600/80 rounded-full -z-10"
                  initial={false}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
